/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import OpenedSvg from '../../../static/icons/Opened';
import ClosedSvg from '../../../static/icons/Closed';
import Badge from './badge';
import { Fragment, useRef, useState } from 'react';
import {
  AllowedType,
  getIsRequired,
  getPropertyTypeInfo,
  getSchemaReferers,
  getTypeNameFromReference
} from '../../utils/schema-extractor';
import { colors, fancyBorder } from '../../theme';
import configSchema from '../../../@generated/current-config-schema.json';
import { buildApiReferenceTableId, capitalize } from '../../utils/misc';
import { onSm } from '../../styles/responsive';
import sortBy from 'lodash/sortBy';
import useCollapse from 'react-hook-collapse';

export type BadgeInfo = {
  isCompositeType: boolean;
  typeName: string;
  isEnum?: boolean;
  exactValue?: string | number;
};

type PropertyTypeInfo = BadgeInfo & {
  isArray: boolean;
  enumeratedValues: (string | number)[];
};

const PropertyTypeBadgeLine = ({ badges }: { badges: BadgeInfo[] }) => {
  return (
    <Fragment>
      {badges.length > 1 && <span css={{ fontWeight: 'bolder', fontSize: '15px', color: colors.font }}>(</span>}
      {badges.map(({ isCompositeType, typeName, isEnum, exactValue }, idx) => {
        const isLast = badges.length - 1 === idx;
        const apiReferenceTableId = isCompositeType && buildApiReferenceTableId({ definitionName: typeName });
        const badgeText = `${typeName}${isEnum ? ' ENUM' : ''}${exactValue ? ` "${exactValue}"` : ''}`;
        return (
          <span key={idx}>
            {isCompositeType ? (
              <a
                onClick={(e) => {
                  e.stopPropagation();
                }}
                css={{ textDecorationColor: colors.font, textDecoration: 'none', lineHeight: 1.3 }}
                href={`#${apiReferenceTableId}`}
              >
                <Badge backgroundColor="#00828b" hoverBackgroundColor={colors.badgeHoverBackground}>
                  {badgeText}
                </Badge>
              </a>
            ) : (
              <Badge backgroundColor={colors.gray}>{badgeText}</Badge>
            )}
            {!isLast && (
              <span>
                &nbsp;
                <span css={{ color: colors.font }}>or</span>
                &nbsp;
              </span>
            )}
          </span>
        );
      })}
      {badges.length > 1 && <span css={{ fontWeight: 'bolder', fontSize: '15px', color: colors.font }}>)</span>}
    </Fragment>
  );
};

const ExpandableView = ({
  propertyTypeInfo,
  longDescription,
  shortDescription
}: {
  propertyTypeInfo;
  longDescription: string;
  shortDescription: string;
}) => {
  const shouldShowPossibleValuesList = propertyTypeInfo.allowedTypes[0].enumeratedValues?.length > 1;
  const shortDesc = shortDescription
    ? shortDescription.startsWith('<p> ')
      ? `<p> ${capitalize(shortDescription.slice(4))}`
      : capitalize(shortDescription)
    : 'No description';

  const allowedPropertyTypes: PropertyTypeInfo[] = propertyTypeInfo.allowedTypes.map(
    ({ typeName, jsonSchemaDefinitionRef, enumeratedValues }) => {
      const exactValue = enumeratedValues?.length === 1 ? enumeratedValues[0] : null;
      return {
        enumeratedValues,
        typeName: jsonSchemaDefinitionRef ? getTypeNameFromReference({ reference: jsonSchemaDefinitionRef }) : typeName,
        isCompositeType: Boolean(jsonSchemaDefinitionRef),
        exactValue,
        isEnum: !exactValue && Boolean(enumeratedValues?.length),
        isArray: propertyTypeInfo.isArray
      };
    }
  );

  const possibleValues = allowedPropertyTypes
    .map(({ enumeratedValues }) => enumeratedValues || [])
    .flat()
    .sort((val1: any, val2: any) => {
      if (typeof val1 === 'number') {
        return Number(val1) - Number(val2);
      }
      return val1;
    });

  return (
    <div
      css={{
        color: colors.font,
        lineHeight: 1.5,
        fontSize: '14px',
        paddingLeft: '7px',
        paddingBottom: '13px'
      }}
    >
      <p
        css={{
          fontSize: '15px',
          li: { lineHeight: '1.4 !important', fontSize: '14px !important' },
          fontWeight: 'bold',
          marginTop: '15px'
        }}
        dangerouslySetInnerHTML={{ __html: shortDesc }}
      />
      <p css={{ fontSize: '14px', color: colors.font, lineHeight: 1.6, marginTop: '13px' }}>
        <span css={{ paddingRight: '3px' }}>Type: </span>
        {propertyTypeInfo.isArray && (
          <span
            css={{
              whiteSpace: 'nowrap',
              [onSm]: {
                whiteSpace: 'normal'
              }
            }}
          >
            Array of&nbsp;
          </span>
        )}
        <PropertyTypeBadgeLine badges={allowedPropertyTypes} />
      </p>
      {shouldShowPossibleValuesList && (
        <p css={{ paddingBottom: '6px', marginTop: '14px', lineHeight: 1.6 }}>
          <span css={{ paddingRight: '3px' }}>Possible values: </span>
          {possibleValues.map((allowedType) => (
            <Badge backgroundColor="#656565">{allowedType}</Badge>
          ))}
        </p>
      )}
      {longDescription ? (
        <p
          css={{
            marginTop: '14px',
            p: { lineHeight: '1.6 !important', fontSize: '14px !important' },
            li: { lineHeight: '1.4 !important', fontSize: '14px !important' }
          }}
          dangerouslySetInnerHTML={{ __html: longDescription }}
        />
      ) : null}
    </div>
  );
};

export const PropertyInfo = ({
  propertyName,
  idx,
  propertyRequired,
  propertyTypeInfo,
  shortDescription,
  longDescription,
  defaultValue
}: {
  // eslint-disable-next-line react/no-unused-prop-types
  propertyName: string;
  idx: number;
  propertyRequired: boolean;
  propertyTypeInfo: {
    allowedTypes: AllowedType[];
    isArray: boolean;
  };
  shortDescription: string;
  longDescription: string;
  defaultValue?: string;
}) => {
  const [open, setOpen] = useState(false);
  const oddRowColor = colors.darkerBackground;
  const evenRowColor = colors.background;
  const ref = useRef();
  useCollapse(ref, open);
  return (
    <div
      css={{
        backgroundColor: idx % 2 === 0 ? evenRowColor : oddRowColor,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative'
      }}
      onClick={() => setOpen(!open)}
      aria-label="expand row"
    >
      <div css={{ width: '100%', padding: '4px 6px 6px 7px' }}>
        <div css={{ display: 'flex', justifyContent: 'space-between', paddingTop: '3px', alignItems: 'center' }}>
          <span
            css={{
              padding: '2.5px 5px',
              fontSize: '14px',
              marginLeft: '2px',
              color: colors.font,
              height: '100%',
              border: '1px solid #767676',
              borderRadius: '4px',
              letterSpacing: 0.8,
              backgroundColor: '#292929'
            }}
          >
            {propertyName}
          </span>
          <div>
            {defaultValue && (
              <span css={{ top: '7px', right: propertyRequired ? '76px' : '8px' }}>
                <Badge backgroundColor="#187e9c">Default: {defaultValue}</Badge>
              </span>
            )}
            {propertyRequired && (
              <span css={{ top: '7px', right: '8px' }}>
                <Badge backgroundColor="#f2720c">Required</Badge>
              </span>
            )}
          </div>
        </div>
        <div ref={ref} css={{ overflow: 'hidden', transition: '0.3s' }}>
          <ExpandableView
            propertyTypeInfo={propertyTypeInfo}
            longDescription={longDescription}
            shortDescription={shortDescription}
          />
        </div>
      </div>
      <div
        css={{
          width: '50px',
          minWidth: '50px',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '8px',
          [onSm]: {
            width: '30px',
            minWidth: '30px'
          },
          'svg path': {
            fill: colors.font
          }
        }}
      >
        {open ? <OpenedSvg /> : <ClosedSvg />}
      </div>
    </div>
  );
};

const getTypenamFromPropertiesInterface = (defName: string) => {
  return defName.endsWith('Props') ? defName.slice(0, -'Props'.length) : defName;
};

const PropertiesHeader = ({
  definitionName,
  referencedInDefinitions
}: {
  referencedInDefinitions: any[];
  definitionName: string;
}) => {
  return (
    <div
      css={{
        display: 'flex',
        padding: '8px 12px',
        background: colors.darkerBackground,
        color: colors.primary,
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '2em'
      }}
    >
      <div css={{ textAlign: 'left', width: '50%' }}>
        <span
          css={{
            fontWeight: 'bolder',
            color: colors.font,
            fontSize: '14px',
            letterSpacing: 0.4,
            borderBottom: 'solid 2px'
          }}
        >
          {getTypenamFromPropertiesInterface(definitionName)}
        </span>
        &nbsp;&nbsp;
        <span css={{ color: colors.font, fontWeight: 'normal' }}>API reference</span>
      </div>
      {referencedInDefinitions.length > 0 && (
        <div css={{ textAlign: 'right', width: '50%' }}>
          <span>Parent API reference: </span>
          <PropertyTypeBadgeLine
            badges={referencedInDefinitions.map((defName) => ({
              typeName: getTypenamFromPropertiesInterface(defName),
              isCompositeType: true
            }))}
          />
        </div>
      )}
    </div>
  );
};

const PropertiesTable = ({
  definitionName,
  searchForReferencesInDefinition
}: {
  definitionName: string;
  searchForReferencesInDefinition?: string;
}) => {
  const { definitions } = configSchema;
  const definition = definitions[definitionName];
  const referencedInDefinitions = getSchemaReferers({
    definitionName,
    definitions: definitions as any,
    searchForReferencesInDefinition
  });

  // const [propertyToNest, nestedPropertyName] = nestedTable.split('.');
  const properties: { propertyName: string; propertySpec: any; required: boolean }[] = [];

  if (!definition) {
    console.error(
      `Definition of ${definitionName} not found. Searching in reference of ${searchForReferencesInDefinition}.`
    );
    return null;
  }

  for (const [propertyName, propertySpec] of Object.entries(definition.properties)) {
    if (!(propertyName === 'properties')) {
      properties.push({
        propertyName,
        propertySpec,
        required: getIsRequired({ definition, propName: propertyName })
      });
    } else {
      const nestedTypeName = getTypeNameFromReference({ reference: (propertySpec as any).$ref });
      const nestedDefinition = definitions[nestedTypeName];
      Object.entries(nestedDefinition.properties).forEach(([nestedPropertyName, nestedPropertySpec]) => {
        properties.push({
          propertyName: `${propertyName}.${nestedPropertyName}`,
          propertySpec: nestedPropertySpec,
          required: getIsRequired({ definition: nestedDefinition, propName: nestedPropertyName })
        });
      });
    }
  }
  const sortedProperties = sortBy(properties, (prop) => prop.required !== true).map((prop) => {
    return {
      ...prop,
      propertyTypeInfo: getPropertyTypeInfo({
        propertySpec: prop.propertySpec,
        propertyName: prop.propertyName,
        definitions: definitions as unknown as { [defName: string]: SchemaNode }
      })
    };
  });

  return (
    <div
      id={buildApiReferenceTableId({ definitionName })}
      css={{
        ...fancyBorder,
        background: colors.background,
        marginBottom: '23px',
        marginTop: '30px'
      }}
    >
      <PropertiesHeader definitionName={definitionName} referencedInDefinitions={referencedInDefinitions} />
      <div>
        {sortedProperties.map(({ propertyName, propertySpec, required, propertyTypeInfo }, idx) => {
          return (
            <PropertyInfo
              key={propertyName}
              propertyName={propertyName}
              idx={idx}
              propertyRequired={required}
              longDescription={propertySpec._MdxDesc?.ld}
              shortDescription={propertySpec._MdxDesc?.sd}
              propertyTypeInfo={propertyTypeInfo}
              defaultValue={propertySpec.default?.[0].text}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PropertiesTable;
